import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2Icon, LayoutGridIcon, UsersIcon } from 'lucide-react';
import type { SpaceVisibilityValue, VisibilityOption } from './spaceVisibility.types';

/**
 * Pure data hook that builds the persona visibility options array.
 *
 * Cross-app/cross-entity reads (workspace name, organization name, permission
 * checks) are LIFTED to the consuming page/feature per Springboard dependency injection —
 * pass them in as args. This keeps `features/audiences` boundary-clean.
 *
 * Internalized into `features/audiences` (was `features/space-visibility`)
 * because audiences was the only consumer and the description copy is
 * persona-specific. KB has its own internal `useKBVisibilityOptions`.
 */
export interface UseVisibilityOptionsArgs {
  /** True if the current user can grant organization-wide visibility. */
  isOrganizationAdmin: boolean;
  /** Display name of the current organization (rendered in the description). */
  organizationName?: string | null;
  /** Display name of the current workspace (rendered in the description). */
  workspaceName?: string | null;
  /**
   * Offers the "specified workspaces" option. Behind `persona_workspace_selection`; without it
   * the choices are the org-wide and single-workspace pair that shipped before.
   */
  canSelectWorkspaces?: boolean;
  /**
   * Offers "this workspace", a create-time shorthand for granting the caller's own. Editing a
   * persona cannot honour it — the persona already has grants, and "my workspace" says nothing
   * about which — so the edit form drops it and names the workspaces outright.
   */
  includeCurrentWorkspace?: boolean;
  /**
   * What the form currently holds. An option that is already the answer is offered even when the
   * user could not choose it: without it the radio group renders with nothing selected, which
   * reads as "unset" for a persona that is anything but.
   */
  currentValue?: SpaceVisibilityValue | null;
}

export const useVisibilityOptions = (args: UseVisibilityOptionsArgs): VisibilityOption[] => {
  const { t } = useTranslation('audiences');
  const {
    isOrganizationAdmin,
    organizationName,
    workspaceName,
    canSelectWorkspaces,
    includeCurrentWorkspace = true,
    currentValue,
  } = args;
  const checkedByDefault = !isOrganizationAdmin;
  const showOrganization = isOrganizationAdmin || currentValue === 'organization';

  return useMemo(
    () =>
      [
        showOrganization
          ? {
              // eslint-disable-next-line i18next/no-literal-string -- visibility scope wire enum value
              value: 'organization',
              label: t('audiences.useVisibilityOptions.organization'),
              description: organizationName
                ? t('audiences.useVisibilityOptions.allUsersUnderOrganization', {
                    organizationName,
                  })
                : t('audiences.useVisibilityOptions.allUsersUnderYourOrganization'),
              icon: Building2Icon,
              checkedByDefault: isOrganizationAdmin,
            }
          : null,
        includeCurrentWorkspace
          ? {
              // eslint-disable-next-line i18next/no-literal-string -- visibility scope wire enum value
              value: 'workspace',
              label: t('audiences.useVisibilityOptions.workspace'),
              description: workspaceName
                ? t('audiences.useVisibilityOptions.onlyUsersUnderWorkspace', { workspaceName })
                : t('audiences.useVisibilityOptions.onlyUsersUnderYourWorkspace'),
              icon: UsersIcon,
              checkedByDefault,
            }
          : null,
        canSelectWorkspaces
          ? {
              // eslint-disable-next-line i18next/no-literal-string -- form-only scope value
              value: 'workspaces',
              label: t('audiences.useVisibilityOptions.specifiedWorkspaces'),
              description: t('audiences.useVisibilityOptions.chooseWhichWorkspaces'),
              icon: LayoutGridIcon,
              checkedByDefault: false,
            }
          : null,
      ].filter(Boolean) as VisibilityOption[],
    [
      organizationName,
      isOrganizationAdmin,
      showOrganization,
      workspaceName,
      checkedByDefault,
      canSelectWorkspaces,
      includeCurrentWorkspace,
      t,
    ],
  );
};
